import { useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { keepPreviousData } from "@tanstack/react-query";
import { type SubmitErrorHandler, useForm } from "react-hook-form";
import { useHistory } from "react-router";

import { toastError, toastSuccess } from "@alpacahq/ui/old";

import {
  getErrorMessage,
  useGetIsLegalEntity,
  useGetTradingAccount,
  useMutationRequest,
} from "~/v2/api/hooks";
import useGetOwners from "~/v2/api/hooks/useGetOwners";
import { submitEntityAccount } from "~/v2/api/rest/entityOnboarding";
import { OnboardingLayout } from "~/v2/components/onboarding";
import { Path } from "~/v2/path";
import {
  DEFAULT_AGREEMENTS_VALUES,
  type EntityOnboardingHistoryState,
} from "../../constants";
import { timer } from "../../timer";
import { usePageTimer } from "../../usePageTimer";
import { logAmplitudeEvent } from "../../util";
import { AgreementsForm } from "./form";
import { type Agreements, zAgreements } from "./schema";

export const AgreementsPage = () => {
  const history = useHistory<EntityOnboardingHistoryState>();

  const form = useForm<Agreements>({
    resolver: zodResolver(zAgreements),
    mode: "onSubmit",
    defaultValues: DEFAULT_AGREEMENTS_VALUES,
  });

  const { account } = useGetTradingAccount();

  const { isLegalEntity, isLegalEntityLoading } = useGetIsLegalEntity();

  const { owners } = useGetOwners({
    accountId: account?.id || "",
    options: {
      placeholderData: keepPreviousData,
      enabled: !!account?.id,
    },
  });

  const { legalEntity, accountOpener, ubos } = useMemo(
    () => ({
      legalEntity: owners?.find((details) => details.is_legal_entity),
      accountOpener: owners?.find((details) => details.is_legal_account_opener),
      ubos: owners?.filter((details) => details.is_ubo) ?? [],
    }),
    [owners],
  );

  const pageTimer = usePageTimer("entity_onboarding_agreements_page");

  const mutation = useMutationRequest(
    () => submitEntityAccount(account?.id ?? ""),
    {
      onSuccess: () => {
        toastSuccess("Account submitted successfully");

        const pageDuration = pageTimer.stop();
        logAmplitudeEvent.pageCompleted(pageDuration);

        const onboardingDuration = timer.end();
        logAmplitudeEvent.onboardingCompleted(onboardingDuration);

        history.push(Path.ROUTE_ENTITY_ONBOARDING_COMPLETED, {
          isEntityOnboardingCompleted: true,
          isInEntityOnboarding: true,
        });
      },
      onError: (err) => {
        const errorMsg = getErrorMessage(err);
        toastError(`Error: ${errorMsg}`);
      },
    },
  );

  const onSubmit = () => {
    mutation.mutate(true);
  };

  const onBackClicked = async () => {
    logAmplitudeEvent.backClicked();
    history.push(Path.ROUTE_ENTITY_ONBOARDING_DOCUMENTS, {
      isInEntityOnboarding: true,
    });
  };

  const onSubmitInvalid: SubmitErrorHandler<Agreements> = (errors) => {
    toastError("Form has errors. Please check the form and try again.");
    const errorMessage = Object.values(errors)
      .map((s) => s.message)
      .join(", ");
    logAmplitudeEvent.invalidInput(errorMessage);
  };

  if (
    account &&
    !isLegalEntity &&
    !isLegalEntityLoading &&
    !history?.location?.state?.isInEntityOnboarding
  ) {
    history.replace(Path.ROUTE_NEW_ACCOUNT);
  }

  if (owners && owners.length >= 1 && !ubos.length) {
    history.replace(Path.ROUTE_ENTITY_ONBOARDING_AUTH_INDIVIDUALS_UBOS, {
      isInEntityOnboarding: true,
    });
  }

  const isLoading =
    !account?.id ||
    !legalEntity?.owner_id ||
    !accountOpener?.owner_id ||
    !ubos.length;

  const isSubmitDisabled = mutation.isPending;

  return (
    <OnboardingLayout title="Agreements" percentage={95} isLoading={isLoading}>
      <AgreementsForm
        accountId={account?.id ?? ""}
        legalEntityOwnerId={legalEntity?.owner_id ?? ""}
        accountOpenerOwnerId={accountOpener?.owner_id ?? ""}
        form={form}
        onBackClicked={onBackClicked}
        onSubmitClicked={form.handleSubmit(onSubmit, onSubmitInvalid)}
        isSubmitDisabled={isSubmitDisabled}
      />
    </OnboardingLayout>
  );
};

export default AgreementsPage;
